import { useEffect } from "react";

function setMetaTag(attr, key, content) {
  if (!content) return null;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  const existed = !!el;
  const prevContent = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return { el, existed, prevContent };
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
}

/**
 * Sets per-page SEO: title, meta description, Open Graph tags, and optional
 * JSON-LD structured data (e.g. schema.org/Product for rich search results —
 * price, image, availability shown directly in Google's results).
 *
 * Restores the previous document state on unmount so navigating away doesn't
 * leave stale tags for the next page to inherit.
 *
 * @param {Object} opts
 * @param {string} opts.title - Page title (shown in browser tab + search results)
 * @param {string} opts.description - Meta description (shown under title in search results)
 * @param {string} [opts.image] - Open Graph image URL for social/share previews
 * @param {Object} [opts.structuredData] - schema.org JSON-LD object (e.g. Product schema)
 */
export function useSEO({ title, description, image, structuredData }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const restored = [];
    const desc = setMetaTag("name", "description", description);
    if (desc) restored.push(desc);
    const ogTitle = setMetaTag("property", "og:title", title);
    if (ogTitle) restored.push(ogTitle);
    const ogDesc = setMetaTag("property", "og:description", description);
    if (ogDesc) restored.push(ogDesc);
    const ogImage = setMetaTag("property", "og:image", image);
    if (ogImage) restored.push(ogImage);

    let jsonLdEl = null;
    if (structuredData) {
      jsonLdEl = setJsonLd("mt-structured-data", structuredData);
    }

    return () => {
      document.title = prevTitle;
      restored.forEach(({ el, existed, prevContent }) => {
        if (existed) el.setAttribute("content", prevContent || "");
        else el.remove();
      });
      if (jsonLdEl) jsonLdEl.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, image, structuredData ? JSON.stringify(structuredData) : null]);
}
